import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { IntlProvider } from 'use-intl';
import { DEFAULT_LOCALE, isLocale, messages, resolveLocale, type Locale } from '@/i18n';
import { applyDocumentLocale, localePath } from '@/app/locale';
import { useSession } from '@/app/session';
import { Spinner } from '@/ui/Button';
import { Shell } from '@/routes/Shell';
import { Dashboard } from '@/routes/Dashboard';
import { Teams } from '@/routes/Teams';
import { Squad } from '@/routes/Squad';
import { Seasons } from '@/routes/Seasons';
import { Import } from '@/routes/Import';
import { Calendar } from '@/routes/Calendar';
import { Exercises } from '@/routes/Exercises';
import { Plan } from '@/routes/Plan';
import { Sessions } from '@/routes/Sessions';
import { Run } from '@/routes/Run';
import { Load } from '@/routes/Load';
import { Matches } from '@/routes/Matches';
import { Objectives } from '@/routes/Objectives';
import { Formations } from '@/routes/Formations';
import { Development } from '@/routes/Development';
import { PlayerFile } from '@/routes/development/PlayerFile';
import { Decisions } from '@/routes/Decisions';
import { Reports } from '@/routes/Reports';
import { Settings } from '@/routes/Settings';
import { Guide } from '@/routes/Guide';
import { Register } from '@/routes/Register';
import { Blocks } from '@/routes/Blocks';
import { Admin } from '@/routes/Admin';
import { Chat } from '@/routes/Chat';
import { Login } from '@/routes/auth/Login';
import { Signup } from '@/routes/auth/Signup';
import { ForgotPassword } from '@/routes/auth/ForgotPassword';
import { ResetPassword } from '@/routes/auth/ResetPassword';
import { VerifyEmail } from '@/routes/auth/VerifyEmail';

/** A URL without a locale gets the best guess from the browser, once. */
function LocaleRedirect() {
  const { pathname, search } = useLocation();
  const locale = resolveLocale(pathname, navigator.languages.join(','));
  return <Navigate to={`${localePath(locale, pathname)}${search}`} replace />;
}

function LocaleLayout() {
  const params = useParams<{ locale: string }>();
  const raw = params.locale ?? DEFAULT_LOCALE;
  const locale: Locale = isLocale(raw) ? raw : DEFAULT_LOCALE;

  useEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  if (!isLocale(raw)) return <Navigate to={localePath(DEFAULT_LOCALE, '/')} replace />;

  return (
    <IntlProvider locale={locale} messages={messages[locale]} timeZone="UTC">
      <LocaleRoutes locale={locale} />
    </IntlProvider>
  );
}

function FullPageLoader() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <Spinner className="text-ink-faint" />
    </div>
  );
}

/** Signed out visitors land on sign-in; the intended page is remembered. */
function RequireSession({ locale }: { locale: Locale }) {
  const { user, loading } = useSession();
  const { pathname } = useLocation();

  if (loading) return <FullPageLoader />;
  if (!user) {
    return (
      <Navigate to={localePath(locale, '/login')} replace state={{ from: pathname }} />
    );
  }
  return <Shell locale={locale} />;
}

/**
 * The pages a signed-in coach has no business being on.
 *
 * Reaching sign-in while already signed in is a dead end at best — the form
 * works, so a coach who meant to switch screens can sign in as somebody else
 * without ever signing out, and the app would then be showing one account's
 * squad under another account's session until the next request. The page is
 * also reachable by browser history, by a stale bookmark, and by the link in
 * an old email, so refusing to render it is the only place this can be fixed.
 *
 * Only the pages that ask for credentials. `reset-password` and `verify-email`
 * carry a token from an email and are most often opened in the browser the
 * coach is already signed in on — bouncing those to the dashboard would make a
 * working reset link look broken.
 */
function RedirectIfSignedIn({ locale, children }: { locale: Locale; children: ReactNode }) {
  const { user, loading } = useSession();

  // Waits for the session rather than rendering the form and taking it away: a
  // sign-in form that appears and vanishes reads as a bug, and a coach fast
  // enough to start typing into it loses what they typed.
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to={localePath(locale, '/')} replace />;
  return <>{children}</>;
}

function LocaleRoutes({ locale }: { locale: Locale }) {
  return (
    <Routes>
      <Route
        path="login"
        element={
          <RedirectIfSignedIn locale={locale}>
            <Login locale={locale} />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="signup"
        element={
          <RedirectIfSignedIn locale={locale}>
            <Signup locale={locale} />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="forgot-password"
        element={
          <RedirectIfSignedIn locale={locale}>
            <ForgotPassword locale={locale} />
          </RedirectIfSignedIn>
        }
      />
      <Route path="reset-password" element={<ResetPassword locale={locale} />} />
      <Route path="verify-email" element={<VerifyEmail locale={locale} />} />

      <Route element={<RequireSession locale={locale} />}>
        <Route index element={<Dashboard locale={locale} />} />
        <Route path="squad" element={<Squad locale={locale} />} />
        <Route path="teams" element={<Teams locale={locale} />} />
        <Route path="seasons" element={<Seasons locale={locale} />} />
        <Route path="import" element={<Import locale={locale} />} />
        <Route path="calendar" element={<Calendar locale={locale} />} />
        <Route path="exercises" element={<Exercises locale={locale} />} />
        <Route path="plan" element={<Plan locale={locale} />} />
        <Route path="sessions" element={<Sessions locale={locale} />} />
        <Route path="run/:sessionId" element={<Run locale={locale} />} />
        <Route path="load" element={<Load locale={locale} />} />
        <Route path="matches" element={<Matches locale={locale} />} />
        <Route path="objectives" element={<Objectives locale={locale} />} />
        <Route path="formations" element={<Formations locale={locale} />} />
        <Route path="development" element={<Development locale={locale} />} />
        <Route path="development/players/:playerId" element={<PlayerFile locale={locale} />} />
        <Route path="decisions" element={<Decisions locale={locale} />} />
        <Route path="reports" element={<Reports locale={locale} />} />
        <Route path="settings" element={<Settings locale={locale} />} />
        <Route path="register" element={<Register locale={locale} />} />
        <Route path="blocks" element={<Blocks locale={locale} />} />
        <Route path="ask" element={<Chat locale={locale} />} />
        <Route path="admin" element={<Admin locale={locale} />} />
        <Route path="guide" element={<Guide locale={locale} />} />
        <Route path="guide/:page" element={<Guide locale={locale} />} />
      </Route>

      <Route path="*" element={<Navigate to={localePath(locale, '/')} replace />} />
    </Routes>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/:locale/*" element={<LocaleLayout />} />
      <Route path="*" element={<LocaleRedirect />} />
    </Routes>
  );
}
